import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  AuthApi,
  btag,
  Bus,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';
import { Button, Icon, Label, Loader, MoneyInput } from '@atlas/ui';

import {
  ACCOUNT_STATUSES,
  bonusStatuses,
  DATA_LAYER_STORAGE_KEYS,
  paymentTypes,
  POSTHOG_CTA,
  transactionStatuses,
} from 'src/common/constants';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Notifications/Popups/alternativePaymentUtils';
import PaymentMethodItem from 'src/components/ClientProfile/PaymentTab/PaymentMethodItem';
import QuickDepositPaymentMethod from 'src/components/QuickDepositPopup/QuickDepositPaymentMethod';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { clampAmountToLimits } from 'src/helpers/clampAmountToLimits';
import { depositSubmit } from 'src/helpers/depositSubmit';
import useFormatter from 'src/hooks/useFormatter';

import { QuickDepositBonusProvider, useQuickDepositBonusContext } from './QuickDepositBonusContext';
import QuickDepositBonuses from './QuickDepositBonuses';

const PAYMENT_HISTORY_ITEMS_COUNT = 30;

export const BUTTON_POSITION = {
  BOTTOM: 'bottom',
  INLINE: 'inline',
};

const QuickDepositFormContent = ({
  buttonPosition = BUTTON_POSITION.INLINE,
  closeQRCodeOnSuccess,
  onClose,
  onDepositStart,
  onDepositSuccess,
  setRedirectUrl,
  titleType,
}) => {
  const {
    account,
    wallet: { currency, detailedBalance },
  } = useApplicationState();

  const { BONUS = 0, CASH = 0, FROZEN = 0 } = detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;

  const history = useHistory();
  const { currencyFormatter } = useFormatter();

  const { payment } = clientProfileRoutes;
  const { deposit: depositIndex } = paymentRoutes;
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);

  const [isInputActive, setIsInputActive] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState(null);
  const {
    activatedBonus,
    amountToGetBonus,
    depositBonuses,
    intendedBonusRef,
    paymentMethod: bonusPaymentMethod,
    selectedBonus,
    setDepositAmount,
    setPaymentMethod: setBonusPaymentMethod,
    setPaymentMethodId,
  } = useQuickDepositBonusContext();
  const [paymentMethod, setPaymentMethod] = useState(bonusPaymentMethod);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [depositPopupId, setDepositPopupId] = useState(null);

  const loadingNotificationId = useRef();

  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.DESKTOP,
  );

  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const { browser, ip, locale, os } = useDetection();

  useEffect(() => {
    AuthApi.getClientPaymentsHistory({
      currentSlice: 0,
      itemsPerSlice: PAYMENT_HISTORY_ITEMS_COUNT,
      operation: paymentTypes.DEPOSIT,
    }).then(({ items }) => {
      const completedMethod =
        items.find(method => method.status === transactionStatuses.SUCCESS) || null;
      setCompletedPaymentMethod(completedMethod);
    });
  }, []);

  useEffect(() => {
    if (!paymentMethod && bonusPaymentMethod) {
      setPaymentMethod(bonusPaymentMethod);
    }
  }, [bonusPaymentMethod, paymentMethod]);

  useEffect(() => {
    if (!paymentMethods.length) return;

    if (completedPaymentMethod?.paymentMethodId) {
      const foundPaymentMethod = paymentMethods.find(
        pm => pm.id === completedPaymentMethod.paymentMethodId,
      );

      if (foundPaymentMethod) {
        setPaymentMethod(foundPaymentMethod);
        setDepositAmount(clampAmountToLimits(completedPaymentMethod.amount, foundPaymentMethod));
        setValue(clampAmountToLimits(completedPaymentMethod.amount, foundPaymentMethod));
        return;
      }
    }

    setPaymentMethod(paymentMethods[0]);
  }, [completedPaymentMethod, paymentMethods, setDepositAmount]);

  useEffect(() => {
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setDepositAmount(paymentMethod.minAmount);
      setValue(paymentMethod.minAmount);
      setError(null);
    } else if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setDepositAmount(paymentMethod.maxAmount);
      setValue(paymentMethod.maxAmount);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, setDepositAmount]);

  useEffect(() => {
    if (setBonusPaymentMethod) {
      if (paymentMethod) {
        setBonusPaymentMethod(paymentMethod);
      }
      return;
    }

    if (paymentMethod) {
      setPaymentMethodId?.(paymentMethod.id);
    }
  }, [paymentMethod, paymentMethod?.id, setBonusPaymentMethod, setPaymentMethodId]);

  const handleValueChange = newValue => {
    setDepositAmount(newValue);
    setValue(newValue);
    if (error) setError(null);
  };

  const handleBlur = useCallback(() => {
    setIsInputActive(false);
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setDepositAmount(paymentMethod.maxAmount);
      setValue(paymentMethod.maxAmount);
    } else if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setDepositAmount(paymentMethod.minAmount);
      setValue(paymentMethod.minAmount);
    }
  }, [paymentMethod, setDepositAmount, value]);

  const onCreateDeposit = () => {
    loadingNotificationId.current = NotificationManager.display({
      data: {
        headerTitle: 'deposit_loading',
        text: 'deposit_loading_text',
        title: 'deposit_loading_title',
      },
      type: 'paymentLoading',
    });
  };

  const onDepositStatusError = useCallback(() => {
    Bus.emit('ui:depositErrorHandled');

    NotificationManager.removeNotification(loadingNotificationId.current);

    const fallbackError = () => {
      NotificationManager.display({
        data: {
          headerTitle: 'deposit',
          text: 'deposit_failed_message',
          title: 'deposit_failed_title',
          withHeader: false,
        },
        type: 'error',
      });
    };

    if (!paymentMethod?.alternativePmEnabled) {
      fallbackError();
      return;
    }

    const depositPageLink = `${clientProfilePath}/${payment}/${depositIndex}`;

    showAlternativeMethodsOrError({
      fallbackError,
      onSelectMethod: method =>
        history.push(buildPaymentUrl(`${depositPageLink}/${method.id}`, value, method)),
      onViewAll: () =>
        history.push(buildPaymentUrl(`${depositPageLink}/${paymentMethods[0].id}`, value, null)),
      operationType: 'DEPOSIT',
      pmId: paymentMethod.id,
    });
  }, [clientProfilePath, depositIndex, history, payment, paymentMethod, paymentMethods, value]);

  const handleDeposit = useCallback(async () => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      history.push(showInPopup ? '?cmd=signup' : staticRoutes.signup);
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED)) {
      pushToDataLayer(
        'qd_deposit_button_click',
        {
          OS: os,
          amount: value,
          bonus_toggle_enabled: !!activatedBonus,
          browser,
          created_at: Date.now(),
          device: constants.DEVICE_TYPES.DESKTOP,
          ip,
          locale,
          page_referer: window.location.pathname,
          payment_method: paymentMethod?.name,
          user_id: account.id,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED, true);
    }

    const bonus = selectedBonus || activatedBonus;
    const isBonusEligible = !!bonus && amountToGetBonus === 0 && !bonus?.disabled;
    const isBonusInstance = bonus?.status === bonusStatuses.ACTIVATED;
    const resolvedBonusId = isBonusInstance ? bonus?.bonus?.id : bonus?.id;
    const bonusId = bonus ? resolvedBonusId : null;

    onDepositStart?.({
      amount: value,
      bonus_id: bonusId,
      cta_clicked: POSTHOG_CTA.DEPOSIT,
      payment_method: paymentMethod?.name ?? null,
    });

    const options = {
      amount: value,
      currency,
      pmId: paymentMethod.id,
      ...(isBonusEligible &&
        !intendedBonusRef.current && {
          additionalAttributes: {
            bonusId: resolvedBonusId,
            bonusInstanceId: isBonusInstance ? bonus.id : null,
          },
        }),
    };

    setLoading(true);

    PaymentV2Api.getPaymentData({
      onCreatePayment: onCreateDeposit,
      onPaymentStatusError: onDepositStatusError,
      paymentOptions: options,
      paymentType: PaymentV2Api.PaymentType.DEPOSIT,
    })
      .then(data => {
        NotificationManager.removeNotification(loadingNotificationId.current);

        pushToDataLayer(
          'deposit_created',
          {
            amount: value,
            client: { id: account.id },
            created_at: Date.now(),
            currency,
            payment_method: data.paymentName,
          },
          btag,
        );

        onDepositSuccess?.({ transaction_id: data.publicPaymentId });

        const notificationId = depositSubmit({
          amount: value,
          data,
          paymentMethod,
          setRedirectUrl,
        });

        setDepositPopupId(notificationId);

        onClose?.();
      })
      .catch(err => {
        if (err?.code) setError({ message: err.code });
      })
      .finally(() => setLoading(false));
  }, [
    account.id,
    account.status,
    activatedBonus,
    amountToGetBonus,
    browser,
    currency,
    history,
    ip,
    locale,
    onClose,
    onDepositStatusError,
    os,
    paymentMethod,
    selectedBonus,
    setRedirectUrl,
    showInPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
    value,
    onDepositStart,
    onDepositSuccess,
  ]);

  const disabledDepositButton = !value || loading;

  useEffect(() => {
    if (!depositPopupId || !closeQRCodeOnSuccess) return;

    if (balance) {
      const timeoutId = setTimeout(() => {
        NotificationManager.removeNotification(depositPopupId);
        setDepositPopupId(null);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [balance, closeQRCodeOnSuccess, depositPopupId]);

  return (
    <>
      <div className="quick-deposit-content">
        {paymentMethod ? (
          <>
            <div className="quick-deposit-form">
              <div className="quick-deposit-input">
                <QuickDepositPaymentMethod
                  isDropdownOpen={isDropdownOpen}
                  paymentMethod={paymentMethod}
                  setIsDropdownOpen={setIsDropdownOpen}
                  withDropdown={paymentMethods.length > 1}
                />
                <MoneyInput
                  active={isInputActive}
                  currency={currency}
                  error={error}
                  label="amount"
                  onBlur={handleBlur}
                  onChange={handleValueChange}
                  onFocus={() => setIsInputActive(true)}
                  value={value}
                />
                {buttonPosition === BUTTON_POSITION.INLINE && (
                  <Button
                    className="deposit-button"
                    disabled={disabledDepositButton}
                    label="deposit"
                    loading={loading}
                    onClick={handleDeposit}
                    size="big"
                  />
                )}
              </div>
              {error && (
                <div className="quick-deposit-form-error">
                  <Icon className="error-icon" height={18} name="error" width={18} />
                  <Label className="error-message" message={error.message} />
                </div>
              )}
            </div>

            <QuickDepositBonuses currencyFormatter={currencyFormatter} titleType={titleType} />

            {buttonPosition === BUTTON_POSITION.BOTTOM && (
              <Button
                className="deposit-button"
                disabled={disabledDepositButton}
                label="deposit"
                loading={loading}
                onClick={handleDeposit}
                size="big"
              />
            )}
          </>
        ) : (
          <Loader />
        )}
      </div>

      {isDropdownOpen && (
        <div className="quick-deposit-payment-methods-dropdown">
          {paymentMethods.map(paymentMethodItem => (
            <button
              className={cx('payment-method-item-wrapper', {
                active: paymentMethod.id === paymentMethodItem.id,
              })}
              key={paymentMethodItem.id}
              onClick={() => {
                setPaymentMethod(paymentMethodItem);
                setIsDropdownOpen(false);
              }}
              type="button"
            >
              <PaymentMethodItem
                depositBonuses={depositBonuses}
                paymentMethod={paymentMethodItem}
              />
            </button>
          ))}
        </div>
      )}
    </>
  );
};

QuickDepositFormContent.propTypes = {
  buttonPosition: PropTypes.oneOf(['inline', 'bottom']),
  closeQRCodeOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onDepositStart: PropTypes.func,
  onDepositSuccess: PropTypes.func,
  setRedirectUrl: PropTypes.func,
  titleType: PropTypes.string,
};

const QuickDepositForm = props => {
  const bonusesContextValue = useQuickDepositBonusContext();

  if (!bonusesContextValue) {
    return (
      <QuickDepositBonusProvider>
        <QuickDepositFormContent {...props} />
      </QuickDepositBonusProvider>
    );
  }

  return <QuickDepositFormContent {...props} />;
};

QuickDepositForm.propTypes = QuickDepositFormContent.propTypes;

export default QuickDepositForm;
